import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { getRaffle, getTickets, subscribeToTickets } from '../services/raffleService';
import { OnDemandLoader } from '../services/onDemandLoader';
import TicketGrid from '../components/TicketGrid';
import RaffleHeader from '../components/RaffleHeader';
import SimpleTicketModal from '../components/SimpleTicketModal';
import SimpleMultiTicketModal from '../components/SimpleMultiTicketModal';
import LoadingSpinner from '../components/LoadingSpinner';
import LanguageSelector from '../components/LanguageSelector';
import { useTranslation } from '../hooks/useTranslation';
import toast from 'react-hot-toast';

const DesktopRafflePage = () => {
  const { raffleId } = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const [raffle, setRaffle] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchNumber, setSearchNumber] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [showMultiModal, setShowMultiModal] = useState(false);
  const [randomIndices, setRandomIndices] = useState([]);
  
  // Get showAll parameter from URL
  const searchParams = new URLSearchParams(location.search);
  const showAllParam = searchParams.get('showAll');
  const showAllTickets = showAllParam !== 'false';
  
  // Debug logging
  console.log('Desktop - URL params:', location.search);
  console.log('Desktop - showAll param value:', showAllParam);
  console.log('Desktop - showAllTickets result:', showAllTickets);

  useEffect(() => {
    loadRaffle();
  }, [raffleId]);

  // Generate stable random indices when tickets change and showAll is false
  useEffect(() => {
    if (!showAllTickets && tickets.length > 0) {
      // Generate random indices only once when tickets are loaded
      if (randomIndices.length === 0) {
        const indices = Array.from({ length: tickets.length }, (_, i) => i);
        const shuffled = indices.sort(() => Math.random() - 0.5);
        const selectedIndices = shuffled.slice(0, Math.min(20, tickets.length));
        setRandomIndices(selectedIndices);
        console.log('Desktop - Generated random indices:', selectedIndices);
      }
    }
  }, [tickets.length, showAllTickets, randomIndices.length]);

  useEffect(() => {
    if (raffleId) {
      const unsubscribe = subscribeToTickets(raffleId, (updatedTickets) => {
        setTickets(updatedTickets);
      });
      return () => unsubscribe();
    }
  }, [raffleId]);

  const loadRaffle = async () => {
    try {
      setLoading(true);
      const raffleData = await getRaffle(raffleId);
      
      if (!raffleData) {
        toast.error(t('raffle.raffleNotFound'));
        return;
      }
      
      setRaffle(raffleData);
      await OnDemandLoader.checkAndLoadTickets(raffleId);
      
      const ticketsData = await getTickets(raffleId);
      
      if (ticketsData.length === 0) {
        setTimeout(async () => {
          const updatedTickets = await getTickets(raffleId);
          setTickets(updatedTickets);
        }, 2000);
      } else {
        setTickets(ticketsData);
      }
    } catch (error) {
      console.error('Error loading raffle:', error);
      toast.error(t('raffle.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleTicketClick = (ticket) => {
    if (ticket.sold || ticket.reserved) {
      toast.error(t('tickets.ticketOccupied'));
      return;
    }
    
    if (isMultiSelectMode) {
      const isSelected = selectedTickets.find(t => t.id === ticket.id);
      if (isSelected) {
        setSelectedTickets(selectedTickets.filter(t => t.id !== ticket.id));
      } else {
        setSelectedTickets([...selectedTickets, ticket]);
      }
    } else {
      setSelectedTicket(ticket);
      setShowModal(true);
    }
  };
  
  const handleRandomSelection = (count) => {
    const availableTickets = tickets.filter(t => !t.sold && !t.reserved);
    
    if (availableTickets.length === 0) {
      toast.error(t('tickets.noAvailableTickets'));
      return;
    }
    
    if (count > availableTickets.length) {
      toast.error(t('tickets.onlyAvailable', { count: availableTickets.length }));
      return;
    }
    
    const shuffled = [...availableTickets].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, count);
    
    setSearchResults(selected);
    setSearchNumber(t('tickets.randomSelection'));
    
    if (isMultiSelectMode) {
      setSelectedTickets([...selectedTickets, ...selected]);
    }
  };
  
  const handleReserveSelected = () => {
    if (selectedTickets.length === 0) {
      toast.error(t('tickets.noTicketsSelected'));
      return;
    }
    
    setShowMultiModal(true);
  };
  
  const handleCloseMultiModal = () => {
    setShowMultiModal(false);
    setSelectedTickets([]);
    setIsMultiSelectMode(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTicket(null);
  };

  // Create a random subset of tickets when showAll is false
  const visibleTickets = useMemo(() => {
    console.log('Desktop - Calculating visibleTickets:', {
      showAllTickets,
      searchNumber: searchNumber.trim(),
      ticketsLength: tickets.length,
      randomIndicesLength: randomIndices.length
    });
    
    if (showAllTickets || searchNumber.trim() !== '') {
      // Show all tickets if showAll is true or if user is searching
      console.log('Desktop - Returning all tickets');
      return tickets;
    }
    
    // Use stable random indices to show the same subset
    if (randomIndices.length > 0) {
      const subset = randomIndices.map(index => tickets[index]).filter(Boolean);
      console.log('Desktop - Returning stable subset:', subset.length, 'tickets');
      return subset;
    }
    
    // Fallback: return empty array until random indices are generated
    console.log('Desktop - Waiting for random indices...');
    return [];
  }, [tickets, showAllTickets, searchNumber, randomIndices]);

  const getFilteredTickets = () => {
    // Use visibleTickets for filtering when not searching
    const ticketsToFilter = searchNumber.trim() !== '' ? tickets : visibleTickets;
    
    switch (filter) {
      case 'available':
        return ticketsToFilter.filter(t => !t.sold && !t.reserved);
      case 'sold':
        return ticketsToFilter.filter(t => t.sold || t.reserved);
      default:
        return ticketsToFilter;
    }
  };

  const getStats = () => {
    const sold = tickets.filter(t => t.sold).length;
    const reserved = tickets.filter(t => t.reserved && !t.sold).length;
    const available = tickets.length - sold - reserved;
    
    return { sold, reserved, available, total: tickets.length };
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!raffle) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            {t('raffle.raffleNotFound')}
          </h1>
          <p className="text-gray-600">
            {t('raffle.verifyLink')}
          </p>
        </div>
      </div>
    );
  }

  const stats = getStats();
  const filteredTickets = getFilteredTickets();

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchNumber(value);
    
    if (value.trim() === '') {
      setSearchResults([]);
      return;
    }
    
    const searchTerms = value.split(',').map(term => term.trim()).filter(term => term !== '');
    const results = tickets.filter(ticket => {
      return searchTerms.some(term => 
        ticket.displayNumbers?.toLowerCase().includes(term.toLowerCase()) ||
        ticket.number.toString() === term
      );
    });
    
    setSearchResults(results);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-50">
        <LanguageSelector />
      </div>
      
      <RaffleHeader raffle={raffle} stats={stats} />
      
      {raffle.isPrivateMode ? (
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {t('privateMode.searchNumbers')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('privateMode.description')}
            </p>
            
            <div className="mb-4">
              <button
                onClick={() => setIsMultiSelectMode(!isMultiSelectMode)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isMultiSelectMode 
                    ? 'bg-primary text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {isMultiSelectMode ? t('tickets.multipleSelectionActiveDesktop') : t('tickets.activateMultipleSelection')}
              </button>
              
              {isMultiSelectMode && selectedTickets.length > 0 && (
                <span className="ml-4 text-sm">
                  {t('tickets.ticketsSelected', { count: selectedTickets.length })}
                  <button
                    onClick={handleReserveSelected}
                    className="ml-4 px-4 py-2 bg-success text-white rounded-lg hover:bg-green-600"
                  >
                    {t('tickets.reserveSelected')}
                  </button>
                </span>
              )}
            </div>
            
            <div className="mb-6">
              <input
                type="text"
                value={searchNumber}
                onChange={handleSearch}
                placeholder={t('tickets.searchPlaceholder')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-2">
                {t('tickets.searchMultiple')}
              </p>
            </div>
            
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">🎲 {t('tickets.randomSelection')}</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleRandomSelection(1)}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                >
                  1 {t('tickets.randomNumbers')}
                </button>
                <button
                  onClick={() => handleRandomSelection(3)}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                >
                  3 {t('tickets.randomNumbers')}
                </button>
                <button
                  onClick={() => handleRandomSelection(5)}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                >
                  5 {t('tickets.randomNumbers')}
                </button>
                <button
                  onClick={() => handleRandomSelection(10)}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                >
                  10 {t('tickets.randomNumbers')}
                </button>
              </div>
            </div>
            
            {searchNumber && (
              <div>
                {searchResults.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {searchResults.map(ticket => (
                      <div
                        key={ticket.id}
                        onClick={() => handleTicketClick(ticket)}
                        className={`p-4 rounded-lg text-center cursor-pointer transition-all transform hover:scale-105 ${
                          ticket.sold 
                            ? 'bg-red-100 text-red-800 cursor-not-allowed' 
                            : ticket.reserved
                            ? 'bg-yellow-100 text-yellow-800 cursor-not-allowed'
                            : selectedTickets.find(t => t.id === ticket.id)
                            ? 'bg-purple-200 text-purple-800 ring-2 ring-purple-500'
                            : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        <div className="text-2xl font-bold">{ticket.displayNumbers}</div>
                        <div className="text-sm mt-1">
                          {ticket.sold ? t('common.sold') : ticket.reserved ? t('common.reserved') : t('common.available')}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {t('tickets.noResultsFor', { search: searchNumber })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <input
                  type="text"
                  value={searchNumber}
                  onChange={handleSearch}
                  placeholder={t('tickets.searchPlaceholder')}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                
                <button
                  onClick={() => setIsMultiSelectMode(!isMultiSelectMode)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    isMultiSelectMode 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {isMultiSelectMode ? t('tickets.multipleSelectionActive') : t('tickets.multipleSelection')}
                </button>
              </div>
              
              <div className="mt-4">
                <span className="text-sm font-medium mr-3">🎲 {t('tickets.randomSelection')}:</span>
                <button
                  onClick={() => handleRandomSelection(1)}
                  className="px-3 py-1 bg-purple-500 text-white rounded hover:bg-purple-600 text-sm mr-2"
                >
                  1
                </button>
                <button
                  onClick={() => handleRandomSelection(3)}
                  className="px-3 py-1 bg-purple-500 text-white rounded hover:bg-purple-600 text-sm mr-2"
                >
                  3
                </button>
                <button
                  onClick={() => handleRandomSelection(5)}
                  className="px-3 py-1 bg-purple-500 text-white rounded hover:bg-purple-600 text-sm mr-2"
                >
                  5
                </button>
                <button
                  onClick={() => handleRandomSelection(10)}
                  className="px-3 py-1 bg-purple-500 text-white rounded hover:bg-purple-600 text-sm"
                >
                  10
                </button>
              </div>
              
              {isMultiSelectMode && selectedTickets.length > 0 && (
                <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                  <span className="text-sm font-medium">
                    {t('tickets.ticketsSelected', { count: selectedTickets.length })}
                  </span>
                  <button
                    onClick={handleReserveSelected}
                    className="ml-4 px-4 py-2 bg-success text-white rounded-lg hover:bg-green-600 text-sm"
                  >
                    {t('tickets.reserveAll')}
                  </button>
                  <button
                    onClick={() => setSelectedTickets([])}
                    className="ml-2 px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 text-sm"
                  >
                    {t('common.clear')}
                  </button>
                </div>
              )}
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    filter === 'all' 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('common.all')} ({stats.total})
                </button>
                <button
                  onClick={() => setFilter('available')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    filter === 'available' 
                      ? 'bg-success text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('common.available')} ({stats.available})
                </button>
                <button
                  onClick={() => setFilter('sold')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    filter === 'sold' 
                      ? 'bg-danger text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('common.occupied')} ({stats.sold + stats.reserved})
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 pb-8">
            {searchNumber ? (
              searchResults.length > 0 ? (
                <div>
                  <h3 className="text-lg font-semibold mb-4">{t('common.search')}:</h3>
                  <TicketGrid 
                    tickets={searchResults}
                    onTicketClick={handleTicketClick}
                    selectedTickets={selectedTickets}
                    isMultiSelectMode={isMultiSelectMode}
                  />
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {t('tickets.noResultsFor', { search: searchNumber })}
                </div>
              )
            ) : (
              <>
                {!showAllTickets && searchNumber.trim() === '' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-blue-800 text-center">
                      {t('raffle.showingSampleDesktop')}
                    </p>
                  </div>
                )}
                <TicketGrid 
                  tickets={filteredTickets}
                  onTicketClick={handleTicketClick}
                  selectedTickets={selectedTickets}
                  isMultiSelectMode={isMultiSelectMode}
                />
              </>
            )}
          </div>
        </>
      )}

      {showModal && selectedTicket && (
        <SimpleTicketModal
          ticket={selectedTicket}
          raffle={raffle}
          raffleId={raffleId}
          onClose={handleCloseModal}
        />
      )}
      
      {showMultiModal && selectedTickets.length > 0 && (
        <SimpleMultiTicketModal
          tickets={selectedTickets}
          raffle={raffle}
          raffleId={raffleId}
          onClose={handleCloseMultiModal}
        />
      )}
    </div>
  );
};

export default DesktopRafflePage;